import { useEffect, useState } from 'react'
import api from '../../../lib/api'
import type { TimelineEvent, CallAnalysis } from '../../../types'
import { UserRole } from '../../../types'
import { DISPOSITION_LABELS } from '../../../lib/constants'
import { authService } from '../../identity/services/auth'
import {
  Phone, MessageSquare, RefreshCw, Bell, FileText,
  MessageCircle, Mail, PackageOpen, ChevronDown, ChevronUp, Mic,
} from 'lucide-react'
import CallTranscriptDrawer from './CallTranscriptDrawer'

const RESTRICTED_ROLES = new Set<string>([UserRole.TELECALLER, UserRole.TEAM_LEADER])

interface Props {
  leadId: number
}

const EVENT_META: Record<string, {
  Icon: typeof Phone
  color: string
  bg: string
  label: string
}> = {
  CALL:          { Icon: Phone,         color: 'var(--info-text)',          bg: 'var(--info-bg)',              label: 'Call' },
  MESSAGE:       { Icon: MessageSquare, color: 'var(--success-text)',       bg: 'var(--success-bg)',           label: 'WhatsApp' },
  SMS:           { Icon: MessageCircle, color: 'var(--info-text)',          bg: 'var(--info-bg)',              label: 'SMS' },
  EMAIL:         { Icon: Mail,          color: 'var(--brand-primary)',      bg: 'var(--brand-primary-subtle)', label: 'Email' },
  STATUS_CHANGE: { Icon: RefreshCw,     color: 'var(--brand-primary)',      bg: 'var(--brand-primary-subtle)', label: 'Status' },
  REMINDER:      { Icon: Bell,          color: 'var(--warning-text)',       bg: 'var(--warning-bg)',           label: 'Reminder' },
  DOCUMENT:      { Icon: FileText,      color: 'var(--text-tertiary)',      bg: 'var(--surface-subtle)',       label: 'Document' },
  INGESTION:     { Icon: PackageOpen,   color: 'var(--text-tertiary)',      bg: 'var(--surface-subtle)',       label: 'Ingested' },
}

function timeAgo(ts: string): string {
  const diff = Date.now() - new Date(ts).getTime()
  const s = Math.floor(diff / 1000)
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  return `${Math.floor(s / 86400)}d ago`
}

function formatTs(ts: string): string {
  return new Date(ts).toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2 text-xs">
      <span className="shrink-0 font-semibold" style={{ color: 'var(--text-secondary)', minWidth: 96 }}>
        {label}
      </span>
      <span style={{ color: 'var(--text-primary)', wordBreak: 'break-word' }}>{value}</span>
    </div>
  )
}

const EXOTEL_RESTRICTED_FIELDS = new Set(['Caller', 'Customer', 'Recording'])

const SENTIMENT_BADGE: Record<string, string> = {
  positive: 'badge-success',
  neutral:  'badge-info',
  negative: 'badge-danger',
}

function CallEventDetail({ details, restricted }: { details: Record<string, unknown>; restricted: boolean }) {
  const disp = details.disposition as string | null
  const status = details.status as string | null
  const dur = details.duration_seconds as number | null
  const remarks = details.remarks as string | null
  const discount = details.od_discount_offered as number | null
  const approvalStatus = details.approval_status as string | null
  const exotel = details.exotel as Record<string, string> | null | undefined
  const analysis = details.analysis as import('../../../types').CallAnalysis | null | undefined

  const visibleExotel = exotel
    ? Object.entries(exotel).filter(([label]) => !restricted || !EXOTEL_RESTRICTED_FIELDS.has(label))
    : []

  return (
    <div
      className="mt-2 rounded-xl p-3 space-y-1.5"
      style={{ background: 'var(--surface-subtle)', border: '1px solid var(--border-default)' }}
    >
      {disp && <DetailRow label="Disposition" value={DISPOSITION_LABELS[disp] ?? disp.replace(/_/g, ' ')} />}
      {status && <DetailRow label="Call status" value={status.replace(/_/g, ' ')} />}
      {dur != null && <DetailRow label="Duration" value={`${dur}s`} />}
      {remarks && <DetailRow label="Remarks" value={remarks} />}
      {discount != null && <DetailRow label="OD discount" value={`${discount}%`} />}
      {approvalStatus && <DetailRow label="Approval" value={approvalStatus.replace(/_/g, ' ')} />}

      {/* Inline AI overview — shows key signals without opening the drawer */}
      {analysis && (
        <div
          className="mt-2 pt-2 space-y-1.5"
          style={{ borderTop: '1px solid var(--border-default)' }}
        >
          <p className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
            AI Overview
          </p>
          {analysis.customer_sentiment && (
            <div className="flex items-center gap-2 text-xs">
              <span className="shrink-0 font-semibold" style={{ color: 'var(--text-secondary)', minWidth: 96 }}>Sentiment</span>
              <span className={SENTIMENT_BADGE[analysis.customer_sentiment] ?? 'badge-info'} style={{ fontSize: 11, textTransform: 'capitalize' }}>
                {analysis.customer_sentiment}
              </span>
            </div>
          )}
          {analysis.customer_intent && (
            <div className="flex gap-2 text-xs">
              <span className="shrink-0 font-semibold" style={{ color: 'var(--text-secondary)', minWidth: 96 }}>Intent</span>
              <span style={{ color: 'var(--text-primary)' }}>{analysis.customer_intent}</span>
            </div>
          )}
          {analysis.agent_score?.overall != null && (
            <div className="flex items-center gap-2 text-xs">
              <span className="shrink-0 font-semibold" style={{ color: 'var(--text-secondary)', minWidth: 96 }}>Agent score</span>
              <span
                className="font-semibold"
                style={{
                  color: analysis.agent_score.overall >= 7
                    ? 'var(--success-text)'
                    : analysis.agent_score.overall >= 4
                      ? 'var(--warning-text)'
                      : 'var(--danger-text)',
                }}
              >
                {analysis.agent_score.overall}/10
              </span>
            </div>
          )}
          {analysis.follow_up_action && (
            <div className="flex gap-2 text-xs">
              <span className="shrink-0 font-semibold" style={{ color: 'var(--text-secondary)', minWidth: 96 }}>Follow-up</span>
              <span style={{ color: 'var(--brand-primary)' }}>{analysis.follow_up_action}</span>
            </div>
          )}
        </div>
      )}

      {visibleExotel.length > 0 && (
        <>
          <div
            className="mt-2 mb-1 text-xs font-bold uppercase tracking-wider flex items-center gap-1"
            style={{ color: 'var(--text-tertiary)' }}
          >
            <Mic className="h-3 w-3" /> Telephony Details
          </div>
          {visibleExotel.map(([label, value]) =>
            label === 'Recording' ? (
              <div key={label} className="flex gap-2 text-xs">
                <span className="shrink-0 font-semibold" style={{ color: 'var(--text-secondary)', minWidth: 96 }}>
                  Recording
                </span>
                <audio src={value} controls preload="none" style={{ height: 32, maxWidth: '100%' }} />
              </div>
            ) : (
              <DetailRow key={label} label={label} value={value} />
            )
          )}
        </>
      )}
    </div>
  )
}

function EventDetail({ details }: { details: Record<string, unknown> | null }) {
  if (!details) return null
  const rows = Object.entries(details).filter(([, v]) => v !== null && v !== undefined && v !== '')
  if (rows.length === 0) return null
  return (
    <div
      className="mt-2 rounded-xl p-3 space-y-1.5"
      style={{
        background: 'var(--surface-subtle)',
        border: '1px solid var(--border-default)',
      }}
    >
      {rows.map(([k, v]) => (
        <div key={k} className="flex gap-2 text-xs">
          <span
            className="shrink-0 font-semibold capitalize"
            style={{ color: 'var(--text-secondary)', minWidth: 90 }}
          >
            {k.replace(/_/g, ' ')}
          </span>
          <span style={{ color: 'var(--text-primary)', wordBreak: 'break-word' }}>
            {typeof v === 'object' ? JSON.stringify(v, null, 2) : String(v)}
          </span>
        </div>
      ))}
    </div>
  )
}

interface DrawerState {
  description: string
  transcript: string | null | undefined
  analysis: CallAnalysis | null | undefined
}

function TimelineRow({
  event, isLast, restricted, role, onOpenDrawer,
}: {
  event: TimelineEvent
  isLast: boolean
  restricted: boolean
  role: string
  onOpenDrawer: (state: DrawerState) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const meta = EVENT_META[event.event_type] ?? EVENT_META.DOCUMENT
  const { Icon, color, bg, label } = meta
  const hasDetails = Boolean(
    event.details &&
    Object.values(event.details).some(v => v !== null && v !== undefined && v !== '')
  )

  const d = (event.details ?? {}) as Record<string, unknown>
  const hasIntelligence = event.event_type === 'CALL' && !!(d.transcript || d.analysis) && role !== UserRole.TELECALLER

  return (
    <div className="flex gap-3">
      {/* Left: icon + connector line */}
      <div className="flex flex-col items-center shrink-0" style={{ width: 36 }}>
        <span
          className="flex items-center justify-center rounded-full shrink-0"
          style={{ width: 36, height: 36, background: bg }}
        >
          <Icon className="h-4 w-4" style={{ color }} />
        </span>
        {!isLast && (
          <div
            className="w-px flex-1 mt-1"
            style={{ background: 'var(--border-default)', minHeight: 24 }}
          />
        )}
      </div>

      {/* Right: content */}
      <div className="pb-5 flex-1 min-w-0">
        {/* Row 1: badge + timestamp + actions */}
        <div className="flex items-center justify-between gap-2 mb-1">
          <span
            className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
            style={{ color, background: bg }}
          >
            {label}
          </span>

          <div className="flex items-center gap-1 shrink-0">
            <span
              className="text-xs"
              style={{ color: 'var(--text-tertiary)' }}
              title={formatTs(event.timestamp)}
            >
              {timeAgo(event.timestamp)}
            </span>

            {/* Speech bubble — opens transcript/AI drawer */}
            {hasIntelligence && (
              <button
                onClick={() => onOpenDrawer({
                  description: event.description,
                  transcript: d.transcript as string | null,
                  analysis: d.analysis as CallAnalysis | null,
                })}
                aria-label="View transcript & AI analysis"
                title="View transcript & AI analysis"
                style={{
                  padding: 6,
                  borderRadius: 6,
                  color: 'var(--brand-primary)',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <MessageCircle className="h-4 w-4" />
              </button>
            )}

            {hasDetails && (
              <button
                onClick={() => setExpanded(p => !p)}
                aria-label={expanded ? 'Collapse' : 'Expand'}
                style={{
                  padding: 6,
                  borderRadius: 6,
                  color: 'var(--text-tertiary)',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {expanded
                  ? <ChevronUp className="h-4 w-4" />
                  : <ChevronDown className="h-4 w-4" />
                }
              </button>
            )}
          </div>
        </div>

        {/* Row 2: description */}
        <p
          className="text-sm leading-snug"
          style={{ color: 'var(--text-primary)', wordBreak: 'break-word' }}
        >
          {event.description}
        </p>

        {/* Row 3: expanded details */}
        {expanded && (
          event.event_type === 'CALL'
            ? <CallEventDetail details={event.details as Record<string, unknown>} restricted={restricted} />
            : <EventDetail details={event.details} />
        )}
      </div>
    </div>
  )
}

export default function EnquiryTrackingCard({ leadId }: Props) {
  const [events, setEvents]   = useState<TimelineEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState('')
  const role = authService.getCurrentUser()?.role ?? ''
  const restricted = RESTRICTED_ROLES.has(role)

  const [drawer, setDrawer] = useState<DrawerState | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    api.get(`/leads/${leadId}/timeline`)
      .then(res => { if (!cancelled) setEvents(res.data.events ?? []) })
      .catch(() => { if (!cancelled) setError('Failed to load history') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [leadId])

  if (loading) {
    return (
      <div className="space-y-4 p-1">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex gap-3 animate-pulse">
            <div
              className="rounded-full shrink-0"
              style={{ width: 36, height: 36, background: 'var(--border-default)' }}
            />
            <div className="flex-1 space-y-2 pt-1">
              <div
                className="h-4 rounded-full"
                style={{ background: 'var(--border-default)', width: '40%' }}
              />
              <div
                className="h-3 rounded"
                style={{ background: 'var(--border-default)', width: '75%' }}
              />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="empty-state">
        <PackageOpen className="h-10 w-10 mb-3" style={{ color: 'var(--text-disabled)' }} />
        <p style={{ color: 'var(--danger-text)' }}>{error}</p>
      </div>
    )
  }

  if (events.length === 0) {
    return (
      <div className="empty-state">
        <PackageOpen className="h-10 w-10 mb-3" style={{ color: 'var(--text-disabled)' }} />
        <p>No history yet</p>
      </div>
    )
  }

  return (
    <>
      <div style={{ paddingTop: 4 }}>
        {events.map((event, idx) => (
          <TimelineRow
            key={idx}
            event={event}
            isLast={idx === events.length - 1}
            restricted={restricted}
            role={role}
            onOpenDrawer={setDrawer}
          />
        ))}
      </div>

      <CallTranscriptDrawer
        open={drawer !== null}
        onClose={() => setDrawer(null)}
        callDescription={drawer?.description ?? ''}
        transcript={drawer?.transcript}
        analysis={drawer?.analysis}
      />
    </>
  )
}
